import { createAction } from '@reduxjs/toolkit'
import { delay, ForkEffect, put, select, takeEvery } from 'redux-saga/effects'

import { BUILDING_SETTING_KEYS, TOASTER_TIMER } from '../../../components/projects/project-settings/utils/constants'
import { floorNameToSettingsKey } from '../../../components/projects/project-settings/utils/helpers'
import { Building } from '../../../models/projectBuilding'
import {
    selectActiveBuilding,
    selectBuildings,
    selectClassifiedFloorLevelsForBuilding,
    setBuildings,
} from '../../slices/buildings'
import { setLoading } from '../../slices/common'

export const applyToAllBuildingAction = createAction('applyToAllBuilding')

const WALL_SHEATHING_SIZE_KEY_PREFIX = 'wall_sheathing_size_'

export function* handleApplyToAllBuilding() {
    const buildings: Building[] = yield select(selectBuildings)
    const activeBuilding: Building = yield select(selectActiveBuilding)
    const classifiedFloorLevels: string[] = yield select(selectClassifiedFloorLevelsForBuilding)

    // Build a set of wall_sheathing_size keys for floors classified on the active building
    const classifiedSheathingSizeKeys = new Set(
        classifiedFloorLevels.map(
            (floorName) => `${WALL_SHEATHING_SIZE_KEY_PREFIX}${floorNameToSettingsKey(floorName)}`
        )
    )

    yield put(setLoading(true))

    // added temporary empty lumber settings in case it doesn't exist
    // this will allow copy settings works
    const buildingsWithDefaultSettings: Building[] = buildings.map((building) => {
        return {
            ...building,
            settings: {
                ...building.settings,
                lumber_settings: building.settings?.lumber_settings || {},
                eaves_and_gables_settings: building.settings?.eaves_and_gables_settings || {},
                sheathing: building.settings?.sheathing || {},
            },
        }
    })

    const updatedBuildings = buildingsWithDefaultSettings.map((otherBuilding) => {
        if (otherBuilding.id !== activeBuilding.id) {
            let otherBuildingCopy: Building = { ...otherBuilding }

            Object.keys(otherBuildingCopy.settings).forEach((buildingSetting) => {
                const targetSettings = otherBuildingCopy.settings[buildingSetting]
                const sourceSettings = activeBuilding.settings?.[buildingSetting] ?? {}

                if (targetSettings !== null && targetSettings !== undefined) {
                    for (const setting in sourceSettings) {
                        if (setting === 'rcm_id') {
                            continue
                        }

                        // Only copy wall_sheathing_size_* for floors classified on the active building
                        if (
                            buildingSetting === BUILDING_SETTING_KEYS.SHEATHING &&
                            setting.startsWith(WALL_SHEATHING_SIZE_KEY_PREFIX) &&
                            !classifiedSheathingSizeKeys.has(setting)
                        ) {
                            continue
                        }

                        otherBuildingCopy = {
                            ...otherBuildingCopy,
                            settings: {
                                ...otherBuildingCopy.settings,
                                [buildingSetting]: {
                                    ...otherBuildingCopy.settings[buildingSetting],
                                    [setting]: sourceSettings[setting],
                                },
                            },
                        }
                    }
                }
            })

            return otherBuildingCopy
        }

        return otherBuilding
    })

    yield put(setBuildings({ buildings: updatedBuildings }))

    // just visual effect that we are updating buildings
    yield delay(TOASTER_TIMER)

    yield put(setLoading(false))
}

export function* watchForApplyToAllBuilding(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(applyToAllBuildingAction.type, handleApplyToAllBuilding)
}
